import React from "react";
import {
  FaFlag,
  FaLightbulb,
  FaUsers,
  FaStar,
  FaCheckCircle,
  FaEye,
  FaBullseye,
} from "react-icons/fa";

/* ─────────────────────────────────────────────────────────────
   Data
───────────────────────────────────────────────────────────── */
const CULTURE_POINTS = [
  { id: "c1", text: "We communicate clearly — in a world crowded with over-the-top marketing, simple wins."          },
  { id: "c2", text: "We are diligent professionals who get your projects delivered on time, every time."             },
  { id: "c3", text: "Selling a product is easy; extracting real business value from it is what makes us different."  },
];

const COMMITMENT_POINTS = [
  { id: "cm1", text: "We are the trusted partner that takes a proactive approach to your technology challenges." },
  { id: "cm2", text: "We are fellow business professionals who understand your day-to-day objectives."           },
  { id: "cm3", text: "We are the built-in IT support team that is always there when you need us."               },
];

const VISION_MISSION = [
  {
    id:    "vision",
    icon:  <FaEye />,
    label: "Vision",
    text:  "Amidst the rise of countless IT companies, our goal was never just to claim we are different — but to demonstrate it by creating a customer-centric organisation built on trust and commitment. Fulfilling customer expectations was, and continues to be, our core motto.",
  },
  {
    id:    "mission",
    icon:  <FaBullseye />,
    label: "Mission",
    text:  "Our vision and mandate drive us to harness intellectual and technical excellence for the right business needs. At Skywise Technologies, we recognise that organisations mirror their people, and we continuously strive to build a proactive, customer-focused company.",
  },
];

const DHARMA_VALUES = [
  { id: "d1", icon: <FaFlag />,     title: "Commitment", desc: "Be accountable, work as a team, and deliver more than you commit.",                              accent: "#2563eb", bg: "rgba(37,99,235,0.08)"  },
  { id: "d2", icon: <FaLightbulb />,title: "Innovation",  desc: "Simplify complex technologies and make them business-friendly for every client.",               accent: "#7c3aed", bg: "rgba(124,58,237,0.08)" },
  { id: "d3", icon: <FaUsers />,    title: "Community",  desc: "Create a career-friendly place for hardworking and talented professionals to thrive.",           accent: "#059669", bg: "rgba(5,150,105,0.08)"  },
  { id: "d4", icon: <FaStar />,     title: "Excellence", desc: "Deliver effective solutions that make a meaningful difference in day-to-day operations.",        accent: "#ea580c", bg: "rgba(234,88,12,0.08)"  },
];

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
function VMC() {
  return (
    <>
      <style>{`
        /*
         * System font stack — no external download.
         *   macOS / iOS  → San Francisco (-apple-system)
         *   Windows      → Segoe UI
         *   Android      → Roboto
         *   Fallback     → Helvetica → Arial → sans-serif
         *
         * Both --font-display (headings) and --font-body (body)
         * use the same stack for a clean, unified, professional look.
         */

        :root {
          --vmc-accent:     #2563eb;
          --vmc-accent2:    #00c2ff;
          --vmc-primary:    #0d3c55;
          --vmc-bg:         #f0f4f9;
          --vmc-card:       #ffffff;
          --vmc-text-dark:  #0f172a;
          --vmc-text-body:  #475569;
          --vmc-text-muted: #94a3b8;
          --vmc-border:     #e2e8f0;
          --vmc-shadow:     0 4px 24px rgba(0,0,0,0.07);
          --vmc-shadow-h:   0 10px 32px rgba(0,0,0,0.13);
          --vmc-radius:     14px;

          --font-display: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          --font-body:    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        }

        .vmc-section {
          background: var(--vmc-bg);
          font-family: var(--font-body);
          padding: 5rem 0;
        }

        .vmc-container {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        /* ── Section header ── */
        .vmc-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }
        .vmc-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          font-family: var(--font-body);
          font-size: 0.74rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vmc-accent);
          background: rgba(37,99,235,0.08);
          border: 1px solid rgba(37,99,235,0.2);
          padding: 0.3rem 0.95rem;
          border-radius: 50px;
          margin-bottom: 1rem;
        }
        .vmc-title {
          font-family: var(--font-display);
          font-size: clamp(1.7rem, 4vw, 2.4rem);
          font-weight: 600;
          color: var(--vmc-text-dark);
          margin: 0 0 0.6rem;
          line-height: 1.3;
        }
        .vmc-title span {
          background: linear-gradient(90deg, var(--vmc-accent), #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .vmc-subtitle {
          font-family: var(--font-body);
          font-size: 1rem;
          font-weight: 400;
          color: var(--vmc-text-body);
          max-width: 520px;
          margin: 0 auto;
          line-height: 1.75;
        }

        /* ── Culture / Commitment split ── */
        .vmc-split {
          display: flex;
          flex-wrap: wrap;
          border-radius: var(--vmc-radius);
          overflow: hidden;
          box-shadow: var(--vmc-shadow);
          margin-bottom: 3rem;
        }
        .vmc-panel {
          flex: 1 1 300px;
          padding: 2.5rem 2.25rem;
        }
        .vmc-panel-teal { background: linear-gradient(135deg, #0d3c55 0%, #1a5c82 100%); color: #fff; }
        .vmc-panel-dark { background: linear-gradient(135deg, #060f24 0%, #0d2352 100%); color: #fff; }

        .vmc-panel-heading {
          font-family: var(--font-display);
          font-size: 1.35rem;
          font-weight: 600;
          color: #fff;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          line-height: 1.3;
        }
        .vmc-panel-heading::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.15);
        }
        .vmc-panel-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--vmc-accent2);
          flex-shrink: 0;
        }

        .vmc-check-list {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .vmc-check-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          font-family: var(--font-body);
          font-size: 0.92rem;
          font-weight: 400;
          color: rgba(255,255,255,0.82);
          line-height: 1.65;
        }
        .vmc-check-icon {
          color: var(--vmc-accent2);
          font-size: 0.95rem;
          margin-top: 3px;
          flex-shrink: 0;
        }

        /* ── Vision / Mission ── */
        .vmc-vm-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 1.25rem;
          margin-bottom: 3rem;
        }
        .vmc-vm-card {
          flex: 1 1 300px;
          background: var(--vmc-card);
          border: 1.5px solid var(--vmc-border);
          border-radius: var(--vmc-radius);
          padding: 1.75rem;
          box-shadow: var(--vmc-shadow);
          position: relative;
          overflow: hidden;
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .vmc-vm-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--vmc-accent), #7c3aed);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
        .vmc-vm-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--vmc-shadow-h);
          border-color: var(--vmc-accent);
        }
        .vmc-vm-card:hover::before { transform: scaleX(1); }

        .vmc-vm-icon-wrap {
          width: 44px; height: 44px;
          border-radius: 10px;
          background: rgba(37,99,235,0.08);
          border: 1px solid rgba(37,99,235,0.18);
          color: var(--vmc-accent);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          margin-bottom: 1rem;
          transition: transform 0.2s;
        }
        .vmc-vm-card:hover .vmc-vm-icon-wrap { transform: scale(1.1) rotate(-4deg); }

        .vmc-vm-label {
          font-family: var(--font-body);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vmc-accent);
          margin-bottom: 0.4rem;
        }
        .vmc-vm-title {
          font-family: var(--font-display);
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--vmc-text-dark);
          margin-bottom: 0.75rem;
          line-height: 1.3;
        }
        .vmc-vm-text {
          font-family: var(--font-body);
          font-size: 0.9rem;
          font-weight: 400;
          color: var(--vmc-text-body);
          line-height: 1.75;
          margin: 0;
        }

        /* ── Dharma heading ── */
        .vmc-dharma-header { margin-bottom: 1.5rem; }
        .vmc-dharma-label {
          font-family: var(--font-body);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vmc-accent);
          margin-bottom: 0.4rem;
        }
        .vmc-dharma-title {
          font-family: var(--font-display);
          font-size: clamp(1.3rem, 2.5vw, 1.7rem);
          font-weight: 600;
          color: var(--vmc-text-dark);
          margin-bottom: 0.4rem;
          line-height: 1.3;
        }
        .vmc-dharma-underline {
          width: 48px; height: 3px;
          background: var(--vmc-accent);
          border-radius: 2px;
          margin-bottom: 0.9rem;
        }
        .vmc-dharma-desc {
          font-family: var(--font-body);
          font-size: 0.95rem;
          font-weight: 400;
          color: var(--vmc-text-body);
          line-height: 1.75;
          max-width: 600px;
        }

        /* ── Dharma cards grid ── */
        .vmc-dharma-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
        }
        .vmc-dharma-card {
          background: var(--vmc-card);
          border: 1.5px solid var(--vmc-border);
          border-radius: 12px;
          padding: 1.5rem 1.25rem;
          position: relative;
          overflow: hidden;
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .vmc-dharma-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: var(--d-accent, #2563eb);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
        .vmc-dharma-card:hover {
          transform: translateY(-5px);
          box-shadow: var(--vmc-shadow-h);
          border-color: var(--d-accent, #2563eb);
        }
        .vmc-dharma-card:hover::before { transform: scaleX(1); }

        .vmc-dharma-icon {
          width: 42px; height: 42px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          margin-bottom: 0.9rem;
          color: var(--d-accent, #2563eb);
          background: var(--d-bg, rgba(37,99,235,0.08));
          border: 1px solid var(--d-accent, #2563eb);
          transition: transform 0.2s ease;
        }
        .vmc-dharma-card:hover .vmc-dharma-icon { transform: scale(1.1) rotate(-4deg); }

        .vmc-dharma-card h5 {
          font-family: var(--font-display);
          font-size: 0.97rem;
          font-weight: 600;
          color: var(--vmc-text-dark);
          margin-bottom: 0.45rem;
          line-height: 1.3;
        }
        .vmc-dharma-card p {
          font-family: var(--font-body);
          font-size: 0.84rem;
          font-weight: 400;
          color: var(--vmc-text-body);
          line-height: 1.65;
          margin: 0;
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .vmc-dharma-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .vmc-section     { padding: 3rem 0; }
          .vmc-panel       { padding: 2rem 1.5rem; }
          .vmc-dharma-grid { grid-template-columns: 1fr; }
          .vmc-vm-card     { flex: 1 1 100%; }
        }
      `}</style>

      <section className="vmc-section" aria-labelledby="vmc-heading">
        <div className="vmc-container">

          {/* ── Header ── */}
          <div className="vmc-header">
            <span className="vmc-eyebrow">Who We Are</span>
            <h2 className="vmc-title" id="vmc-heading">
              Our Vision, Mission &amp; <span>Dharma</span>
            </h2>
            <p className="vmc-subtitle">
              Empowering businesses through innovation, integrity, and excellence —
              at every step of the journey.
            </p>
          </div>

          {/* ── Culture / Commitment split ── */}
          <div className="vmc-split" role="region" aria-label="Culture and Commitment">
            <div className="vmc-panel vmc-panel-teal">
              <h3 className="vmc-panel-heading">
                <span className="vmc-panel-dot" aria-hidden="true" />
                Our Culture
              </h3>
              <ul className="vmc-check-list">
                {CULTURE_POINTS.map(({ id, text }) => (
                  <li key={id} className="vmc-check-item">
                    <FaCheckCircle className="vmc-check-icon" aria-hidden="true" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="vmc-panel vmc-panel-dark">
              <h3 className="vmc-panel-heading">
                <span className="vmc-panel-dot" aria-hidden="true" />
                Our Commitment
              </h3>
              <ul className="vmc-check-list">
                {COMMITMENT_POINTS.map(({ id, text }) => (
                  <li key={id} className="vmc-check-item">
                    <FaCheckCircle className="vmc-check-icon" aria-hidden="true" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Vision & Mission ── */}
          <div className="vmc-vm-grid" role="region" aria-label="Vision and Mission">
            {VISION_MISSION.map(({ id, icon, label, text }) => (
              <div key={id} className="vmc-vm-card">
                <div className="vmc-vm-icon-wrap" aria-hidden="true">{icon}</div>
                <div className="vmc-vm-label">{label}</div>
                <h3 className="vmc-vm-title">{label} Statement</h3>
                <p className="vmc-vm-text">{text}</p>
              </div>
            ))}
          </div>

          {/* ── Dharma (Values) ── */}
          <div role="region" aria-label="Core values">
            <div className="vmc-dharma-header">
              <div className="vmc-dharma-label">Core Values</div>
              <h3 className="vmc-dharma-title">Our Dharma</h3>
              <div className="vmc-dharma-underline" />
              <p className="vmc-dharma-desc">
                Our working-class values are the driving force behind our excellent
                performance — shaping every decision, delivery, and relationship we build.
              </p>
            </div>

            <div className="vmc-dharma-grid">
              {DHARMA_VALUES.map(({ id, icon, title, desc, accent, bg }) => (
                <div
                  key={id}
                  className="vmc-dharma-card"
                  style={{ "--d-accent": accent, "--d-bg": bg }}
                >
                  <div className="vmc-dharma-icon" aria-hidden="true">{icon}</div>
                  <h5>{title}</h5>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>
    </>
  );
}

export default VMC;